import { captureException } from '@fabric-app/compat/capabilities/sentry';
import { createLogger } from '@fabric-app/utils/logging';
import { ObservableV2 } from '../../../node_modules/lib0/observable.js';
import { create } from '../../../node_modules/lib0/promise.js';
import * as Y from 'yjs';
import { simpleHashUint8Array } from '@fabric-app/utils/text';
import { isDefined, isString } from '@fabric-app/utils/guards';
import { NOTEPAD_VERSION } from '../../Tiptap/tiptap.config.js';
import { compareVersions } from 'compare-versions';

const docChangeMeta = [
  "addToHistory"
];
const getWasDocChanged = (tr, origin) => {
  return origin instanceof Y.UndoManager || Array.from(tr.meta.entries()).filter(([key, value]) => value === true && docChangeMeta.includes(key)).length > 0;
};
var HybridSyncProviderMode = /* @__PURE__ */ function(HybridSyncProviderMode2) {
  HybridSyncProviderMode2[HybridSyncProviderMode2["FullyOffline"] = 0] = "FullyOffline";
  HybridSyncProviderMode2[HybridSyncProviderMode2["SemiOffline"] = 1] = "SemiOffline";
  HybridSyncProviderMode2[HybridSyncProviderMode2["Online"] = 2] = "Online";
  return HybridSyncProviderMode2;
}({});
class HybridSyncProvider extends ObservableV2 {
  /**
  * Creates an instance of HybridSyncProvider.
  * @param {HybridSyncProviderOptions} options - The options for the provider.
  */
  constructor(options) {
    super();
    this.logger = createLogger(`HybridSyncProvider#${options.doc.guid.slice(-5)}`);
    this.doc = options.doc;
    this.debounceWait = options.debounceWait || 1e3;
    this.synced = false;
    this.version = NOTEPAD_VERSION;
    this.outdated = false;
    this._destroyed = false;
    this._pendingUpdates = [];
    this._updateTimeout = null;
    this.mode = options.initialMode || 0;
    this.doc.on("update", this._storeUpdate.bind(this));
    this.doc.on("destroy", this.destroy.bind(this));
    this.whenSynced = create((resolve) => this.on("synced", () => resolve(this)));
    this.logger.logDevOnly("HybridSyncProvider created");
  }
  _consumePendingUpdates() {
    const pendingUpdates = [
      ...this._pendingUpdates
    ];
    this._pendingUpdates = [];
    this.emit("pendingUpdatesChange", [
      this._pendingUpdates
    ]);
    return pendingUpdates;
  }
  _pushPendingUpdate(update, docChanged) {
    if (this._destroyed) {
      captureException(new Error("(OFFLINE NOTEPAD): Received update after destroy"));
      return;
    }
    this._pendingUpdates.push({
      update,
      hash: simpleHashUint8Array(update).toString(36),
      docChanged
    });
    this.emit("pendingUpdatesChange", [
      this._pendingUpdates
    ]);
  }
  updateVersion() {
    const meta = this.doc.getMap("meta");
    const version = meta.get("version");
    if (!isDefined(version) || !isString(version) || compareVersions(version, NOTEPAD_VERSION) < 0) {
      this.doc.transact((tr) => {
        tr.meta.set("updateVersion", true);
        meta.set("version", NOTEPAD_VERSION);
      });
      this.version = NOTEPAD_VERSION;
    } else if (compareVersions(version, NOTEPAD_VERSION) > 0) {
      this.version = version;
      this.outdated = true;
      this.emit("outdated", [
        this
      ]);
    } else {
      this.version = version;
      this.outdated = false;
    }
  }
  /**
  * Initializes the provider with the given initial state.
  * Will emit the 'synced' event when complete.
  * @param {Uint8Array | Promise<Uint8Array>} initialState - The initial state to apply.
  * @returns {Promise<void>}
  */
  async initialize(initialState) {
    if (this.synced) {
      this.logger.logDevOnly("already synced");
      return;
    }
    const state = await Promise.resolve(initialState);
    Y.applyUpdate(this.doc, state, this);
    this.updateVersion();
    if (this.outdated) return;
    this.synced = true;
    this.emit("synced", [
      this
    ]);
    this.logger.logDevOnly("initialized and emitted synced");
  }
  /**
  * Stores an update based on the current mode.
  * @private
  * @param {Uint8Array} update - The update to store.
  * @param {unknown} origin - The origin of the update.
  */
  _storeUpdate(update, origin, _, tr) {
    if (this._destroyed) {
      captureException(new Error("(OFFLINE NOTEPAD): Received update after destroy"));
      return;
    }
    if (origin === this) return;
    this.updateVersion();
    const wasDocChanged = getWasDocChanged(tr, origin);
    switch (this.mode) {
      case 2:
      case 0:
        this.logger.logDevOnly("storing update in pending updates and debouncing send", origin);
        this._pushPendingUpdate(update, wasDocChanged);
        this._debouncedSendUpdates();
        break;
      case 1:
        this.logger.logDevOnly("storing update in pending updates", origin);
        this._pushPendingUpdate(update, wasDocChanged);
        break;
    }
  }
  /**
  * Debounces the sending of updates.
  * @private
  */
  _debouncedSendUpdates() {
    if (this._updateTimeout) {
      clearTimeout(this._updateTimeout);
    }
    if (this._destroyed) {
      captureException(new Error("(OFFLINE NOTEPAD): Debounced send updates after destroy"));
      return;
    }
    this._updateTimeout = window.setTimeout(() => {
      this._sendUpdates();
    }, this.debounceWait);
  }
  /**
  * Sends the pending updates.
  * @private
  */
  _sendUpdates() {
    if (this._destroyed) {
      captureException(new Error("(OFFLINE NOTEPAD): Send updates after destroy"));
      return;
    }
    const updates = this._consumePendingUpdates();
    if (updates.length > 0) {
      const mergedUpdate = Y.mergeUpdates(updates.map((u) => u.update));
      const hashes = updates.map((u) => u.hash);
      this.emit("update", [
        mergedUpdate,
        hashes,
        updates.some((u) => u.docChanged)
      ]);
      this.logger.logDevOnly("sent updates");
    }
  }
  /**
  * Gets the current state of the document.
  * @returns {Uint8Array} The encoded state of the document.
  */
  getDocState() {
    return Y.encodeStateAsUpdate(this.doc);
  }
  /**
  * Applies an update to the document.
  * @param {Uint8Array} update - The update to apply.
  */
  applyUpdate(update) {
    this.logger.logDevOnly("applying external update");
    Y.applyUpdate(this.doc, update, this);
  }
  /**
  * Destroys the provider and returns any pending updates.
  * @returns {Uint8Array | null} The final update or null if no pending updates.
  */
  destroy() {
    this.logger.logDevOnly("destroying");
    this._destroyed = true;
    this.doc.off("update", this._storeUpdate);
    this.doc.off("destroy", this.destroy);
    if (this._updateTimeout) {
      clearTimeout(this._updateTimeout);
      this._updateTimeout = null;
    }
    const finalUpdates = this._consumePendingUpdates();
    if (finalUpdates.length > 0) {
      this.logger.logDevOnly("sending final update");
      const finalUpdate = Y.mergeUpdates(finalUpdates.map((u) => u.update));
      const hashes = finalUpdates.map((u) => u.hash);
      const docChanged = finalUpdates.some((u) => u.docChanged);
      this.emit("update", [
        finalUpdate,
        hashes,
        docChanged
      ]);
      return {
        update: finalUpdate,
        hashes,
        docChanged
      };
    }
    return null;
  }
  /**
  * Allows consuming the pending updates without destroying the provider.
  * e.g. on window unload, if the user cancels the page close the provider would be
  * destroyed and the further updates would be lost.
  * @returns {Uint8Array | null} The final update or null if no pending updates.
  */
  consumePendingUpdates() {
    const finalUpdates = this._consumePendingUpdates();
    if (finalUpdates.length > 0) {
      const finalUpdate = Y.mergeUpdates(finalUpdates.map((u) => u.update));
      const hashes = finalUpdates.map((u) => u.hash);
      const docChanged = finalUpdates.some((u) => u.docChanged);
      return {
        update: finalUpdate,
        hashes,
        docChanged
      };
    }
    return null;
  }
  /**
  * Sets the mode of the provider.
  * @param {HybridSyncProviderMode} newMode - The mode to set.
  */
  setMode(newMode) {
    this.mode = newMode;
    if (newMode === 0 && this._pendingUpdates.length > 0) {
      this._debouncedSendUpdates();
    }
    this.emit("modeChange", [
      this.mode
    ]);
    this.logger.logDevOnly("mode changed to", newMode);
  }
}

export { HybridSyncProvider, HybridSyncProviderMode, getWasDocChanged };
//# sourceMappingURL=HybridSyncProvider.js.map
